<template>
  <div class="content-main" v-if="importStatus">
    <div class="content-main__centered">
      <div v-if="importFinished">
        <h4 class="appMain--text mb-1">Success!</h4>
        <p class="mb-6">Your contacts have been uploaded.</p>
      </div>
      <div v-else>
        <p class="mb-1">
          Your import into
          <strong>{{ importStatus.bucket_title }}</strong> has a status of
          <v-chip label color="primary" small class="pa-2 pt-0 pb-0 ml-1 mr-1">
            {{ status }}
          </v-chip>
        </p>
        <p class="mb-6">
          Once we have finished importing your file, we will start merging into
          contacts.
        </p>
      </div>

      <v-data-table
        class="status-table styled-table mb-6"
        :headers="headers"
        :items="tableItems"
        :loading="loading"
        hide-default-footer
        :itemsPerPage="-1"
        :mobile-breakpoint="0"
      />

      <div class="mt-4" v-if="!hideTagGroup">
        <p class="mb-2">Create a group for fresh contacts with a tag:</p>
        <div v-for="tag in importedContactsTags" :key="tag + 'i'" class="mb-2">
          <v-btn
            color="appMain"
            class="tag-button"
            small
            @click="createGroupFor(tag)"
          >
            {{ tag }}
          </v-btn>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { format } from 'date-fns';

export default {
  name: 'ContactsImportStatus',
  props: {
    importId: {
      type: String,
      default: '',
    },
    hideTagGroup: Boolean,
  },
  data() {
    return {
      headers: [
        {
          text: 'Results',
          value: 'status',
          sortable: false,
          width: 160,
        },
        {
          text: '',
          value: 'count',
          sortable: false,
        },
      ],
      loading: false,
      importStatus: {},
      statusOrder: ['imported', 'pending', 'finished', 'failed'],
    };
  },
  computed: {
    ...mapGetters('fields', ['getSingleField', 'loaded']),
    tableItems() {
      if (!this.importStatus) return [];
      return Object.keys(this.importStatus.stats || {})
        .reduce((acc, key) => {
          if (key === 'locked') return acc;
          acc.push({
            status: key === 'sent' ? 'finished' : key,
            count: this.importStatus.stats[key],
          });
          return acc;
        }, [])
        .sort((a, b) => {
          return (
            this.statusOrder.indexOf(a.status) -
            this.statusOrder.indexOf(b.status)
          );
        });
    },
    importFinished() {
      const stats = this.importStatus?.stats;
      if (!stats) return false;

      if (Object.values(this.importStatus?.stats).every((stat) => stat === 0)) {
        return false;
      }
      return !stats.pending;
    },
    status() {
      if (this.importFinished) return 'Finished';
      return this.importStatus.status;
    },
    importedContactsTags() {
      const currentTags = this.importStatus?.tags || [];
      const defaultTag = 'import_' + format(new Date(), 'MM_dd_yy');
      return [...currentTags, defaultTag];
    },
  },
  methods: {
    ...mapActions(['getContactsImportStatus']),
    ...mapActions('fields', ['load']),
    async checkStatus() {
      await this.$nextTick();

      this.loading = true;

      try {
        this.importStatus = await this.getContactsImportStatus(this.importId);
        this.setReloadTimeout();
      } finally {
        this.loading = false;
      }
    },

    setReloadTimeout() {
      if (this.importFinished) return;

      setTimeout(() => {
        this.checkStatus();
      }, 2000);
    },

    createGroupFor(tag) {
      const { bucketId, name } = this.getSingleField('tags') || {};
      const field = `${bucketId}.${name}`;

      this.$router.push({
        name: 'group-create',
        params: {
          outerFilter: {
            $and: [{ [field]: { $in: tag } }],
          },
        },
      });
    },

    checkFields() {
      if (this.loaded) return;
      this.load();
    },
  },
  mounted() {
    this.checkFields();
    setTimeout(() => {
      this.checkStatus();
    }, 300);
  },
};
</script>

<style lang="less">
.status-table {
  tbody {
    tr:nth-child(odd):hover,
    tr:nth-child(odd) {
      background-color: var(--v-lightGray-base) !important;
    }
  }
  tr {
    cursor: default;
    &:hover {
      background-color: #fff !important;
    }
  }
  td {
    text-transform: capitalize;
    font-size: 14px !important;
  }
}

.v-btn.tag-button {
  text-transform: none;
}
</style>
